import { queryOptions, useQuery } from '@tanstack/react-query';

import { COUNTRY_COOKIE } from '@/app/consts/cookies';
import useCurrency from '@/app/hooks/useCurrency';
import { graphql, type ResultOf, type VariablesOf } from '@/graphql';
import { getCartIdCookie } from '@/utils/cartUtils';
import { getCookie } from '@/utils/cookie';
import gqlClient from '@/utils/gqlClient';

import productFragment from './fragments/ProductFragment';
import mapProductFragmentToListProduct from './utils/mapProductFragmentToListProduct';

const query = graphql(
  `
    query Product(
      $productKey: String!
      $cartId: String
      $region: String
      $currency: String
      $flags: [String]
      $includeInventory: Boolean
      $countryCode: String
    ) {
      content(
        region: $region
        currency: $currency
        enabledFeatureFlags: $flags
      ) {
        product(productKey: $productKey) {
          ...ProductFragment
        }
      }
    }
  `,
  [productFragment],
);

export type ProductResult = ResultOf<typeof query>;
export type ProductVariables = VariablesOf<typeof query>;

export const productOptions = (vars: ProductVariables) => {
  // Automatically include cartId if not provided
  if (!vars.cartId) {
    vars.cartId = getCartIdCookie();
  }

  // Include country from cookies if none was provided to ensure we're getting correct inventory for user's country
  if (vars.includeInventory && !vars.countryCode) {
    const countryCookie = getCookie(COUNTRY_COOKIE);
    vars.countryCode = countryCookie;
  }

  return queryOptions({
    queryKey: ['Product', { vars }],
    queryFn: async () => {
      const data = await gqlClient().request(query, vars);
      return data.content?.product
        ? mapProductFragmentToListProduct(data.content.product)
        : null;
    },
  });
};

export function useProductData({
  productKey,
  includeInventory = false,
}: {
  productKey: string;
  includeInventory?: boolean;
}) {
  const currency = useCurrency();
  return useQuery({
    ...productOptions({
      currency: currency.currencyCode,
      includeInventory,
      productKey,
    }),
  });
}
